import { Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { ApiService } from 'src/app/core/services/api.service';
import { environment } from 'src/environment/environment';

@Injectable({ providedIn: 'root' })
export class DocumentProcessingApiService extends ApiService {
  constructor() {
    super();
  }

  pageReview$(
    id: string,
    pageNumber: number,
    playbookId: string,
  ): Observable<DocumentProcessingPagedReviewResponse> {
    console.log(`Reviewing page ${pageNumber} of doc ${id} `);
    //if (pageNumber === 3) {
    //return of(new HttpErrorResponse({ status: 500, error: "Page 3 of document review is failed" })).pipe(delay(1000));
    //}

    //'https://localhost:7054/DocumentProcessing/page-review?documentUniqueId=eea6b309-f141-446b-aa81-9f1e771d15c9&include=legal_differences&pageNumber=1&playbookId=fe9e779e-0ccc-4fed-8edd-b4768cfddff7' \

    let url = `/documentProcessing/page-review?documentUniqueId=${id}&pageNumber=${pageNumber}&include=legal_differences&include=legal_consequences&include=proofs_page_numbers`;
    //let url = `/documentProcessing/page-review?documentUniqueId=${id}&pageNumber=${pageNumber}&playbookId=${playbookId}`;

    if (playbookId) {
      url += `&playbookId=${playbookId}`;
    }
    return this.get(url).pipe(
      map((response: any) => response as DocumentProcessingPagedReviewResponse),
    );
  }

  pageReviewNoIncludes$(
    id: string,
    pageNumber: number,
    assignedPlaybookId: string,
  ): Observable<DocumentProcessingPagedReviewResponse> {
    console.log(`Reviewing page ${pageNumber} of doc ${id} `);

    let url = `/documentProcessing/page-review?documentUniqueId=${id}&pageNumber=${pageNumber}`;

    if (assignedPlaybookId) {
      url += `&playbookId=${assignedPlaybookId}`;
    }

    return this.get(url).pipe(
      map((response: any) => response as DocumentProcessingPagedReviewResponse),
    );
  }

  getSuggestion$(
    documentUniqueId: string,
    clauseId: number,
    suggestionType: ClauseOptionType,
  ): Observable<string> {
    return this.getText(
      `/documentProcessing/get-suggestion?documentUniqueId=${documentUniqueId}&clauseId=${clauseId}&suggestionType=${suggestionType}`,
    );
  }

  applySuggestion$(
    documentUniqueId: string,
    clauseId: number,
    suggestion: string,
    alignWithClauseType: ClauseOptionType,
  ): Observable<SuggestionExtract[]> {
    const model = {
      documentUniqueId,
      clauseId,
      suggestion,
      alignWithClauseType,
    } as ApplySuggestion;
    return this.post(`/documentProcessing/apply-suggestion`, model);
  }

  getQuestions$(id: string): Observable<DocumentProcessingQuestion[]> {
    return this.get(`/documentProcessing/questions?documentUniqueId=${id}`);
  }

  askQuestion$(docId: string, text: string): Observable<DocumentProcessingQuestion> {
    return this.get(`/documentProcessing/ask-question?documentUniqueId=${docId}&question=${text}`);
  }

  askKeyQuestion$(docId: string, questionId: number): Observable<DocumentProcessingQuestion> {
    return this.get(
      `/documentProcessing/ask-key-question?documentUniqueId=${docId}&questionId=${questionId}`,
    );
  }

  generateClause$(id: string, text: string): Observable<GeneratedClause> {
    return this.get(
      `/documentProcessing/generate-clause?documentUniqueId=${id}&clauseDescription=${text}`,
    );
  }

  modifyClause$(model: ModifyClause): Observable<ModifiedClauseResponse> {
    return this.post(`/documentProcessing/modify-clause`, model);
    //return of({ header: "Modified clause header", text: `Modified clause text for ${text}` }).pipe(delay(300));
  }

  getSummary$(id: string): Observable<SummaryResponse> {
    return this.get(`/documentProcessing/get-summary?documentUniqueId=${id}`);
  }

  discardCache$(guid: string): Observable<any> {
    return this.post(`/documentProcessing/discard-cache/${guid}`);
  }

  getCommentOnSuggestions$(model: CommentOnSuggestionsRequestDto): Observable<any> {
    return this.post(`/documentProcessing/comment-on-suggestions`, model);
  }

  getCommentOnChanges$(model: CommentOnChangesRequestDto): Observable<any> {
    return this.post(`/documentProcessing/comment-on-changes`, model);
  }

  getAnalysis$(id: string): Observable<AnalysisResponse> {
    return this.get(`/documentProcessing/structural-analysis?documentUniqueId=${id}`);
  }

  getRegulatoryCompliance$(id: string): Observable<RegulatoryComplianceResponse> {
    return this.get(`/documentProcessing/regulatory-compliance-analysis?documentUniqueId=${id}`);
  }

  getReadabilityAnalysis$(id: string): Observable<ReadabilityAnalysisResponse> {
    return this.get(`/documentProcessing/readability-analysis?documentUniqueId=${id}`);
  }
}

export interface ReadabilityAnalysisResponse {
  overview: string;
  issues: ReadabilityAnalysisIssue[];
  complexTerms: ReadabilityAnalysisComplexTerm[];
  assessment: ReadabilityAnalysisAssessment;
}

export interface ReadabilityAnalysisAssessment {
  linguistic: {
    score: number;
    analysis: string;
  };
  structural: {
    score: number;
    analysis: string;
  };
  pedagogical: {
    score: number;
    analysis: string;
  };
  globalScore: number;
  verdict: string;
}

export interface ReadabilityAnalysisIssue {
  title: string;
  category: ReadabilityAnalysisIssueCategory;
  riskLevel: ReadabilityAnalysisIssueRiskLevel;
  contractReference: string;
  problem: string;
  impact: string;
  suggestion: string;
  plainLanguageExample: string;
  supportMaterials: string[];
  relatedLegalArticles: string[];
  showSupportingMaterials?: boolean; // extended property for UI to show/hide supporting materials section
}

export interface ReadabilityAnalysisComplexTerm {
  term: string;
  context: string;
  explanation: string;
  suggestedReplacement: string;
  usageExample: string;
}

export enum ReadabilityAnalysisIssueCategory {
  Unknown = 0,
  Readability = 1,
  Accessibility = 2,
  Pedagogy = 3,
}

export enum ReadabilityAnalysisIssueRiskLevel {
  Unknown = 0, // red
  Critical = 1, // red
  Major = 2, // red
  Moderate = 3, // orange
  Minor = 4, // orange
}

export interface RegulatoryComplianceResponse {
  jurisdiction: RegulatoryComplianceJursidiction;
  risks: RegulatoryComplianceRisk[];
  summary: RegulatoryComplianceSummary;
}

export interface RegulatoryComplianceJursidiction {
  identifiedLaw: string;
  reason: string;
  sources: string[];
  regulatoryBodies: string[];
}

export interface RegulatoryComplianceRisk {
  issueType: RegulatoryComplianceIssueType;
  clauseReference: string;
  clauseExcerpts: string[];
  explanation: string;
  legalReference: RegulatoryComplianceLegalReference;
  riskLevel: RequlatoryRiskLevel;
  reasoning: string;
  suggestion: string;
  showLegalReference?: boolean; // extended property for UI to show/hide legal reference section
}

export interface RegulatoryComplianceLegalReference {
  legislation: string;
  jurisprudence: string;
  regulatoryGuidance: string;
}

export interface RegulatoryComplianceSummary {
  overallRiskAssessment: number;
  keyObservations: string;
}

export interface CommentOnSuggestionsRequestDto {
  documentUniqueId: string;
  contentToReplace: string;
  mostDesiredOptionText: string;
  fallbackOptionText?: string;
}

export interface CommentOnChangesRequestDto {
  documentUniqueId: string;
  suggestion: string;
  originalExtract: string;
  modifiedExtract: string;
}

export interface CommentOnSuggestionsResponseDto {
  text: string;
}

export interface SummaryResponse {
  text: string;
}

export interface DocumentProcessingPagedReviewResponse {
  documentUniqueId: string;
  clauses: DocumentProcessingClause[];
  pageNumber: number;
  nextPageNumber: number;
  totalPages: number;
}

export interface GeneratedClause {
  header: string;
  text: string;
}

export interface SuggestionExtract {
  applyingCurrentSuggestion?: boolean; // extended property for ui
  applyDisabled?: boolean; // extended property for ui related to failed wordContentService.selectContent operation
  applyAllDisabled?: boolean; // used for Apply All button disabled state
  applyResult?: SuggestionExtractApplyResult; // extended property for ui related to failed apply suggestion operation and show error text
  originalExtract: string;
  modifiedExtract: string;
  paragraphsIds?: string[]; // used for apply(all) buttons state related to WordApi 1.6 paragraphChangedHandler
}

export enum SuggestionExtractApplyResult {
  Success = 1,
  Failed = 2,
}

export interface ApplySuggestion {
  documentUniqueId: string;
  clauseId: number;
  suggestion: string;
  alignWithClauseType: ClauseOptionType;
}

export interface ModifyClause {
  documentUniqueId: string;
  clauseText: string;
  instruction: string;
}

export interface ModifiedClauseResponse {
  clauseText: string;
}

export interface DocumentProcessingClause {
  id: number;
  name?: string;
  header: string;
  explanation: string;
  suggestion?: string;
  flagType: FlagType;
  reviewResponse: ReviewClauseResponse;
  proofPosition?: number;
  activeSuggestionTabType?: ClauseOptionType; // Align to Most Desired | Align to Fallback
  includeExplanatoryComment?: boolean;
  applyingSomeClauseSuggestion?: boolean;
  proofs: string[];
  playbookOptions: PlaybookOption[];
  previewingChanges?: boolean; // used for Preview Changes panel open/closed state
  applySuggestionMostDesiredResponse?: SuggestionExtract[]; // result of Preview Changes -> documentProcessing/apply-suggestion api call
  applySuggestionFallbackResponse?: SuggestionExtract[]; // result of Preview Changes -> documentProcessing/apply-suggestion api call
  modifyClauseResponse?: string; // result of Preview Changes of blue flag item -> documentProcessing/modify-clause api call
  previewChangePosition?: number; // used for Preview Changes tab navigation by items
  previewChangeTab?: PreviewChangeTab; // used inside Preview Changes panel
  showingPlaybook?: boolean; // used for Show Playbook panel open/closed state
  showingPlaybookTab?: ClauseOptionType; // used for
  proofPageNumbers: number[]; // clause variance analysis
  legalDifferences: string; // clause variance analysis
  legalConsequences: string; // clause variance analysis
}

export interface ReviewClauseResponse {
  documentUniqueId: string;
  clause: ReviewClauseResponseItem;
}

export interface AnalysisResponse {
  summary: string;
  integrityIssues: IntegrityIssue[];
}

export interface IntegrityIssue {
  title: string;
  riskLevel: IntegrityIssueRiskLevel;
  issueType: IntegrityAnalysisIssueType;
  explanation: string;
}

export interface ShortReviewClauseResponseItem {
  id: number;
  name: string;
}

export interface ReviewClauseResponseItem {
  id: number;
  name?: string;
  header: string;
  flagType: FlagType;
  optionText: string;
  proofs: string[];
  suggestion?: string;
  explanation: string;
  requiringFallbackSuggestion?: boolean; // can be used for fallback suggestion field retrieving by extra api call state
  fallbackSuggestionClientSide?: string; // used for fallback suggestion field retrieving by extra api call
  fallbackOptionCannotBeProvided?: boolean; // used to indicate that playbook lacks Fallback option
}

export interface Suggestion {
  text: string;
  type: ClauseOptionType;
}

export interface PlaybookOption {
  text: string;
  type: ClauseOptionType;
}

export interface DocumentProcessingQuestion {
  id: number;
  header: string;
  answer: string;
  proofs?: string[];
  proofPosition?: number;
}

export enum FlagType {
  MostDesired = 0, // Green
  Unacceptable = 1, // Red
  Fallback = 2, // Orange
  Missing = 3, // Blue
}

export enum ClauseOptionType {
  MostDesired = 1,
  Fallback = 2,
  Unacceptable = 3,
}

export enum PreviewChangeTab {
  Original = 1,
  Suggested = 2,
  Comparison = 3,
}

export const apiDocumentProcessingProtectedResources = [
  {
    httpMethod: 'GET',
    scopes: [...environment.protectedResources.apiDocumentProcessing.scopes.read],
  },
  {
    httpMethod: 'POST',
    scopes: [...environment.protectedResources.apiDocumentProcessing.scopes.write],
  },
  {
    httpMethod: 'PUT',
    scopes: [...environment.protectedResources.apiDocumentProcessing.scopes.write],
  },
  {
    httpMethod: 'DELETE',
    scopes: [...environment.protectedResources.apiDocumentProcessing.scopes.write],
  },
];

export enum IntegrityIssueRiskLevel {
  Low = 1,
  Medium = 2,
  High = 3,
}

export enum RequlatoryRiskLevel {
  Low = 1,
  Medium = 2,
  High = 3,
}

export enum IntegrityAnalysisIssueType {
  LogicalContradiction = 1,
  DraftingAmbiguity = 2,
  DefinitionIssue = 3,
  StructuralInconsistency = 4,
  BrokenReference = 5,
  IncompleteDrafting = 6,
  Other = 7,
}

export enum RegulatoryComplianceIssueType {
  Unknown = 0,
  LegalAndRegulatoryCompliance = 1,
  CommercialAndFinancial = 2,
  OperationalAndPerformance = 3,
  LiabilityAndIndemnification = 4,
  GovernanceAndProcedural = 5,
  EnforceabilityAndValidity = 6,
}
